/**
 * Derive Component Colors from Global Palette
 *
 * Takes a simple 6-color palette and generates full component color schemes
 */

import { isLightColor, getContrastText, darken, lighten, createHoverColor } from './colorUtils';
import type { BrandPackageColors } from '../api/modules/brandPackages';

/**
 * Global color palette that users configure
 */
export interface GlobalPalette {
  primary: string; // Main brand color (buttons, links, accents)
  secondary: string; // Supporting color (cards, subtle accents)
  background: string; // Main page/section background
  text: string; // Primary text color
  accent: string; // Highlights, special elements
  dark: string; // Dark sections (navbar, footer)
}

/**
 * Default palette for new brand packages
 */
export const DEFAULT_PALETTE: GlobalPalette = {
  primary: '#3b82f6', // Blue
  secondary: '#6366f1', // Indigo
  background: '#ffffff', // White
  text: '#111827', // Near black
  accent: '#f59e0b', // Amber
  dark: '#1f2937', // Gray-800
};

/**
 * Derive full component colors from a global palette
 */
export function deriveComponentColors(palette: GlobalPalette): BrandPackageColors {
  const { primary, secondary: _secondary, background, text, accent, dark } = palette;

  // Determine if we're in a "dark mode" scenario
  const isDarkBackground = !isLightColor(background);

  // Create subtle background variants (noticeable contrast for visual rhythm)
  const subtleBackground = isDarkBackground ? lighten(background, 20) : darken(background, 12);

  const cardBackground = isDarkBackground ? lighten(background, 8) : '#ffffff';

  return {
    // Navbar - uses dark color
    navbar: {
      background: dark,
      text: getContrastText(dark),
      brandText: getContrastText(dark),
      linkText: isLightColor(dark) ? darken(dark, 30) : lighten(dark, 60),
      linkHover: primary,
      border: isLightColor(dark) ? darken(dark, 10) : lighten(dark, 10),
      // CTA button colors for navbar
      buttonBg: primary,
      buttonText: getContrastText(primary),
      buttonHoverBg: createHoverColor(primary),
    },

    navigationMenu: {
      background: dark,
      text: getContrastText(dark),
      brandText: getContrastText(dark),
      linkText: isLightColor(dark) ? darken(dark, 30) : lighten(dark, 60),
      linkHover: primary,
      border: isLightColor(dark) ? darken(dark, 10) : lighten(dark, 10),
      buttonBg: primary,
      buttonText: getContrastText(primary),
      buttonHoverBg: createHoverColor(primary),
      dropdownBackground: isLightColor(dark) ? '#ffffff' : lighten(dark, 12),
      dropdownText: getContrastText(isLightColor(dark) ? '#ffffff' : lighten(dark, 12)),
      dropdownHover: primary,
    },

    // Hero - uses main background with primary buttons
    hero: {
      background: background,
      text: text,
      buttonBg: primary,
      buttonText: getContrastText(primary),
      buttonHover: createHoverColor(primary),
      // Brand-package palette backgrounds should render as exact colors
      // (no inherited theme darkening overlay/pattern).
      overlay: '',
    },

    // Features - subtle background with cards
    features: {
      background: subtleBackground,
      cardBg: cardBackground,
      iconColor: primary,
      titleColor: text,
      textColor: isDarkBackground ? lighten(text, 30) : darken(background, 50),
      borderColor: isDarkBackground ? lighten(background, 15) : darken(background, 10),
      // CTA button colors
      buttonBg: primary,
      buttonText: getContrastText(primary),
    },

    // Footer - uses dark color
    footer: {
      background: dark,
      text: getContrastText(dark),
      mutedText: isLightColor(dark) ? darken(dark, 40) : lighten(dark, 40),
      linkHover: primary,
    },

    // CTA - uses primary as background
    cta: {
      background: primary,
      text: getContrastText(primary),
      buttonBg: isLightColor(primary) ? dark : '#ffffff',
      buttonText: isLightColor(primary) ? getContrastText(dark) : text,
      buttonHover: isLightColor(primary) ? lighten(dark, 10) : darken('#ffffff', 10),
    },

    // Form - clean form styling
    form: {
      background: background,
      inputBg: cardBackground,
      inputBorder: isDarkBackground ? lighten(background, 20) : darken(background, 15),
      inputText: text,
      labelColor: text,
      buttonBg: primary,
      buttonText: getContrastText(primary),
      buttonHover: createHoverColor(primary),
    },

    // Pricing - cards with primary highlights
    // Calculate highlighted card background first so we can derive text colors from it
    pricing: (() => {
      const highlightedCardBg = isLightColor(primary) ? lighten(primary, 92) : lighten(primary, 88);
      const isHighlightedCardLight = isLightColor(highlightedCardBg);
      // Get appropriate text color for the highlighted card based on its actual background
      const highlightedTextColor = getContrastText(highlightedCardBg);
      const highlightedMutedText = isHighlightedCardLight
        ? darken(highlightedCardBg, 50)
        : lighten(highlightedCardBg, 50);

      return {
        background: subtleBackground,
        cardBg: cardBackground,
        highlightedCard: highlightedCardBg,
        titleColor: text,
        priceColor: primary,
        featureColor: isDarkBackground ? lighten(text, 20) : darken(background, 40),
        checkColor: primary,
        borderColor: isDarkBackground ? lighten(background, 15) : darken(background, 10),
        // Highlighted card text colors - based on the highlighted card's actual background
        highlightedTitle: highlightedTextColor,
        highlightedDescription: highlightedMutedText,
        highlightedPrice: highlightedTextColor,
        highlightedPeriod: highlightedMutedText,
        highlightedFeature: highlightedMutedText,
        highlightedCheckColor: primary,
        highlightText: highlightedTextColor,
        highlightBorder: isLightColor(primary) ? darken(primary, 20) : lighten(primary, 20),
        highlightRing: primary,
        badgeBg: isLightColor(primary) ? darken(primary, 30) : primary,
        badgeText: getContrastText(primary),
        buttonBg: primary,
        highlightedButtonBg: isLightColor(primary) ? darken(primary, 20) : lighten(primary, 10),
      };
    })(),

    // Team - clean cards
    team: {
      background: background,
      cardBg: cardBackground,
      nameColor: text,
      roleColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      borderColor: isDarkBackground ? lighten(background, 15) : darken(background, 10),
      // CTA button colors
      buttonBg: primary,
      buttonText: getContrastText(primary),
    },

    // Media + Text sections
    mediaText: {
      background: background,
      titleColor: text,
      subtitleColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      textColor: isDarkBackground ? lighten(text, 20) : darken(background, 35),
      accentColor: primary,
      buttonBg: primary,
      buttonText: getContrastText(primary),
    },

    // Text blocks
    textBlocks: {
      background: background,
      titleColor: text,
      subtitleColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      textColor: isDarkBackground ? lighten(text, 20) : darken(background, 35),
      linkColor: primary,
      linkHover: createHoverColor(primary),
      // CTA button colors
      buttonBg: primary,
      buttonText: getContrastText(primary),
    },

    // FAQ section
    faq: {
      background: subtleBackground,
      titleColor: text,
      subtitleColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      questionColor: text,
      answerColor: isDarkBackground ? lighten(text, 20) : darken(background, 35),
      itemBackground: cardBackground,
      itemBorder: isDarkBackground ? lighten(background, 15) : darken(background, 10),
      iconColor: primary,
      // CTA button colors
      buttonBg: primary,
      buttonText: getContrastText(primary),
    },

    // Testimonials
    testimonials: {
      background: background,
      titleColor: text,
      subtitleColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      cardBackground: cardBackground,
      cardBorder: isDarkBackground ? lighten(background, 15) : darken(background, 10),
      quoteColor: isDarkBackground ? lighten(text, 20) : darken(background, 35),
      nameColor: text,
      roleColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      starColor: accent,
      // CTA button colors
      buttonBg: primary,
      buttonText: getContrastText(primary),
    },

    // Split Hero - uses main background
    splitHero: {
      background: background,
      text: isDarkBackground ? lighten(text, 20) : darken(background, 35),
      titleColor: text,
      subtitleColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      taglineColor: primary,
      buttonBg: primary,
      buttonText: getContrastText(primary),
      buttonHover: createHoverColor(primary),
    },

    // Split HTML - same tokens as Split Hero text/CTA
    htmlSplit: {
      background: background,
      text: isDarkBackground ? lighten(text, 20) : darken(background, 35),
      titleColor: text,
      subtitleColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      taglineColor: primary,
      buttonBg: primary,
      buttonText: getContrastText(primary),
      buttonHover: createHoverColor(primary),
    },

    // Simple Header - uses main background
    simpleHeader: {
      background: background,
      titleColor: text,
      subtitleColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      // CTA button colors
      buttonBg: primary,
      buttonText: getContrastText(primary),
    },

    // Title Subtitle - uses subtle background for visual contrast
    titleSubtitle: {
      background: subtleBackground,
      titleColor: text,
      subtitleColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      buttonBg: primary,
      buttonText: getContrastText(primary),
    },

    // Contact Info - uses subtle background
    contactInfo: {
      background: subtleBackground,
      titleColor: text,
      textColor: isDarkBackground ? lighten(text, 20) : darken(background, 35),
      accentColor: primary,
      // CTA button colors
      buttonBg: primary,
      buttonText: getContrastText(primary),
    },

    // Video - uses subtle background for visual contrast
    video: {
      background: subtleBackground,
      titleColor: text,
      subtitleColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      textColor: isDarkBackground ? lighten(text, 20) : darken(background, 35),
      buttonBg: primary,
      buttonText: getContrastText(primary),
    },

    // Countdown Timer - uses subtle background for emphasis
    countdownTimer: {
      background: subtleBackground,
      text: text,
      headline: text,
      subheadline: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      number: primary,
      label: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      // CTA button colors
      buttonBg: primary,
      buttonText: getContrastText(primary),
    },

    // Image Card - uses subtle background so cards stand out
    imageCard: {
      background: subtleBackground,
      overlayColor: dark,
      title: '#ffffff',
      description: '#e5e7eb',
      buttonBg: primary,
      buttonText: getContrastText(primary),
    },

    // Image Gallery - uses subtle background
    imageGallery: {
      background: subtleBackground,
      text: text,
      title: text,
      subtitle: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      caption: isDarkBackground ? lighten(text, 20) : darken(background, 35),
      filterButton: isDarkBackground ? lighten(background, 15) : darken(background, 10),
      filterButtonActive: primary,
      // CTA button colors
      buttonBg: primary,
      buttonText: getContrastText(primary),
    },

    // Image Info - uses main background
    imageInfo: {
      background: background,
      text: text,
      title: text,
      subtitle: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      imageBorder: isDarkBackground ? lighten(background, 15) : darken(background, 10),
      // CTA button colors
      buttonBg: primary,
      buttonText: getContrastText(primary),
    },

    // HTML Embed - uses main background
    htmlEmbed: {
      background: background,
    },

    // Promo Banner - uses primary as background for attention
    promoBanner: {
      background: primary,
      text: getContrastText(primary),
      linkText: getContrastText(primary),
      dismissButton: getContrastText(primary),
    },

    // Logo Cloud - uses subtle background with primary accent for title (matches Stats valueColor)
    logocloud: {
      background: subtleBackground,
      titleColor: primary,
      subtitleColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
    },

    // Stats - uses subtle background with accent for numbers
    stats: {
      background: subtleBackground,
      titleColor: text,
      subtitleColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      valueColor: primary,
      labelColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      descriptionColor: isDarkBackground ? lighten(text, 20) : darken(background, 35),
    },

    // Blog Preview - section with cards linking to blog posts (matches Stats/Logocloud pattern)
    blogPreview: {
      background: subtleBackground,
      titleColor: text,
      subtitleColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      cardBackground: cardBackground,
      cardTitle: text,
      cardExcerpt: isDarkBackground ? lighten(text, 25) : darken(background, 35),
      linkColor: primary,
    },

    conversionPopup: {
      overlay: isDarkBackground ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.55)',
      panelBackground: cardBackground,
      headlineColor: text,
      bodyTextColor: isDarkBackground ? lighten(text, 20) : darken(background, 35),
      buttonBackground: primary,
      buttonText: getContrastText(primary),
      closeButton: isDarkBackground ? lighten(text, 35) : '#6b7280',
    },

    // Blog - comprehensive blog styling
    blog: {
      // Page-level backgrounds
      pageBackground: isDarkBackground ? background : subtleBackground,
      headerBackground: cardBackground,
      // Typography
      titleColor: text,
      descriptionColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      textColor: isDarkBackground ? lighten(text, 20) : darken(background, 50),
      mutedTextColor: isDarkBackground ? lighten(text, 40) : darken(background, 35),
      linkColor: primary,
      linkHoverColor: createHoverColor(primary),
      // Cards (post previews)
      cardBackground: cardBackground,
      cardBorder: isDarkBackground ? lighten(background, 15) : darken(background, 10),
      cardShadow: isDarkBackground
        ? '0 1px 3px 0 rgba(0, 0, 0, 0.5)'
        : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      cardHoverShadow: isDarkBackground
        ? '0 10px 15px -3px rgba(0, 0, 0, 0.5)'
        : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      // Post content
      postTitleColor: text,
      postTextColor: isDarkBackground ? lighten(text, 20) : darken(background, 50),
      postMetaColor: isDarkBackground ? lighten(text, 40) : darken(background, 35),
      // Tags
      tagBackground: isDarkBackground ? lighten(background, 15) : darken(background, 8),
      tagText: isDarkBackground ? lighten(text, 20) : darken(background, 50),
      tagActiveBackground: primary,
      tagActiveText: getContrastText(primary),
      // Sidebar
      sidebarBackground: cardBackground,
      sidebarTitleColor: text,
      // Pagination
      paginationBackground: cardBackground,
      paginationText: text,
      paginationBorder: isDarkBackground ? lighten(background, 15) : darken(background, 10),
      paginationActiveBackground: primary,
      paginationActiveText: getContrastText(primary),
      paginationDisabledOpacity: '0.5',
      // Icons
      iconColor: primary,
      // Loading & Error states
      loadingSpinnerColor: primary,
      errorIconColor: isDarkBackground ? lighten(text, 30) : darken(background, 40),
      errorButtonBackground: primary,
      errorButtonText: getContrastText(primary),
    },
  };
}

/**
 * Check if colors object is a GlobalPalette or full BrandPackageColors
 */
export function isGlobalPalette(colors: unknown): colors is GlobalPalette {
  if (!colors || typeof colors !== 'object') return false;
  const palette = colors as Record<string, unknown>;
  return (
    typeof palette.primary === 'string' &&
    typeof palette.secondary === 'string' &&
    typeof palette.background === 'string' &&
    typeof palette.text === 'string' &&
    typeof palette.accent === 'string' &&
    typeof palette.dark === 'string' &&
    !('navbar' in palette) // Full colors would have component keys
  );
}

/**
 * Get component colors - handles both palette and full colors
 */
export function getComponentColors(colors: GlobalPalette | BrandPackageColors): BrandPackageColors {
  if (isGlobalPalette(colors)) {
    return deriveComponentColors(colors);
  }
  return colors;
}
